/**
 * Vivre Control Plane — Reusable Modal Styles
 * 
 * Usage:
 *   import { modalStyles } from 'components/Modal/modalStyles';
 *   <Dialog PaperProps={{ sx: modalStyles.paper }}>
 *     <MDBox sx={modalStyles.header}>...</MDBox>
 *     <MDBox sx={modalStyles.body}>...</MDBox>
 *     <MDBox sx={modalStyles.footer}>...</MDBox>
 *   </Dialog>
 */

export const modalStyles = {
  // Dialog paper override
  paper: {
    borderRadius: "12px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    maxWidth: 520,
    width: "100%",
    overflow: "hidden",
    border: "1px solid #E8EDF2",
  },

  // Wide variant (for forms with more fields)
  paperWide: {
    borderRadius: "12px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    maxWidth: 680,
    width: "100%",
    overflow: "hidden",
    border: "1px solid #E8EDF2",
  },

  // Header with gradient background
  header: {
    background: "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)",
    px: 3,
    py: 2.5,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },

  // Header title text
  headerTitle: {
    color: "#fff",
    fontWeight: 700,
    fontSize: 18,
  },

  // Header subtitle
  headerSubtitle: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 13,
    mt: 0.3,
  },

  // Close button in header
  closeButton: {
    background: "rgba(255,255,255,0.15)",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    borderRadius: "6px",
    padding: "4px 10px",
    fontSize: 16,
    fontWeight: 700,
    transition: "background 0.15s",
    "&:hover": {
      background: "rgba(255,255,255,0.25)",
    },
  },

  // Body content area
  body: {
    px: 3,
    py: 3,
  },

  // Form field wrapper (consistent spacing)
  fieldGroup: {
    mb: 2.5,
  },

  // Field label
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#1E3A5F",
    mb: 0.5,
    display: "block",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },

  // Input styling override
  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#F8FAFC",
      fontSize: 14,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#00ACC4",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#2F36CB",
        borderWidth: "1.5px",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E8EDF2",
    },
  },

  // Select styling
  select: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#F8FAFC",
      fontSize: 14,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E8EDF2",
    },
  },

  // Footer with action buttons
  footer: {
    px: 3,
    py: 2,
    display: "flex",
    justifyContent: "flex-end",
    gap: 1.5,
    borderTop: "1px solid #E8EDF2",
    backgroundColor: "#F8FAFC",
  },

  // Primary action button
  primaryButton: {
    background: "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "9px 24px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    transition: "opacity 0.15s, transform 0.1s",
    "&:hover": {
      opacity: 0.9,
    },
    "&:active": {
      transform: "scale(0.98)",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },

  // Secondary/cancel button
  secondaryButton: {
    background: "#fff",
    color: "#5D6D7E",
    border: "1px solid #E8EDF2",
    borderRadius: "8px",
    padding: "9px 20px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s",
    "&:hover": {
      borderColor: "#BDC3C7",
      backgroundColor: "#F4F6F9",
    },
  },

  // Danger button (for destructive actions)
  dangerButton: {
    background: "#E74C3C",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "9px 20px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    "&:hover": {
      background: "#CB4335",
    },
  },

  // Section divider within modal body
  sectionDivider: {
    my: 2.5,
    borderColor: "#F0F3F6",
  },

  // Helper text
  helperText: {
    fontSize: 11,
    color: "#5D6D7E",
    mt: 0.5,
  },
};

export default modalStyles;
